<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'materialArticleForm',
})
</script>
<template>
  <div class="main">
    <a-card>
      <a-spin :spinning="state.loading.submit || state.loading.detail">
        <div v-if="isDraftLoaded" class="draft-tip">
          <a-tag color="warning">已恢复未保存的草稿</a-tag>
        </div>
        <div class="space-gap" style="height: 10px" />
        <a-form ref="formRef" :model="state.ruleForm" :rules="readonly ? {} : state.rules"
          :label-col="{ style: 'width: 80px' }" :wrapper-col="{ style: 'flex: 1' }">
          <div class="form-grid">
            <a-form-item class="grid-title" label="标题" name="title">
              <a-textarea v-model:value="state.ruleForm.title" placeholder="请输入文章标题" :maxlength="30"
                :show-count="!readonly" :disabled="readonly" :auto-size="false" :rows="2" allow-clear />
            </a-form-item>
            <a-form-item class="grid-storeType" label="店铺类型" name="storeTypeCode">
              <a-select v-model:value="state.ruleForm.storeTypeCode" placeholder="请输入" :disabled="readonly" allow-clear
                @change="changeStoreType">
                <a-select-option v-for="item in storeTypeList" :key="item.code" :value="item.code">{{ item.name
                }}</a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item class="grid-category" label="分类" name="categoryCode">
              <a-select v-model:value="state.ruleForm.categoryCode" placeholder="请选择"
                :disabled="readonly || !state.ruleForm.storeTypeCode" allow-clear>
                <a-select-option v-for="item in categoryList" :key="item.code" :value="item.code">{{ item.name
                }}</a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item class="grid-keyword" label="关键词" name="keyword">
              <a-input v-model:value="state.ruleForm.keyword" placeholder="请输入关键词" :maxlength="50"
                :show-count="!readonly" :disabled="readonly" allow-clear />
            </a-form-item>
            <a-form-item v-if="showSortField" class="grid-sort" label="排序" name="sortOrder">
              <div class="sort-input-number">
                <button class="sort-btn" type="button" :disabled="readonly" @click="onSortChange(-1)">−</button>
                <input class="sort-input" type="number" :value="state.ruleForm.sortOrder" :min="0" :max="9999"
                  :disabled="readonly" placeholder="请输入排序" @input="onSortInput" />
                <button class="sort-btn" type="button" :disabled="readonly" @click="onSortChange(1)">+</button>
              </div>
            </a-form-item>
          </div>
          <div class="space-gap" style="height: 10px" />
          <a-form-item class="grid-content" name="content" :label-col="{ span: 0 }" :wrapper-col="{ span: 24 }">
            <Editor :key="editorKey" ref="editorRef" v-model:getHtml="state.ruleForm.content" :disable="readonly"
              height="400px" :enableVideoUpload="false" />
          </a-form-item>
          <a-form-item v-if="!readonly" class="form-footer">
            <a-button @click="goBack">取 消</a-button>
            <a-button class="ml10" type="primary" :loading="state.loading.submit" @click="onSubmit">
              保存
            </a-button>
          </a-form-item>
          <a-form-item v-else class="form-footer">
            <a-button @click="goBack">返 回</a-button>
          </a-form-item>
        </a-form>
      </a-spin>
    </a-card>
  </div>
</template>

<script setup lang="ts" name="materialArticleForm">
import { reactive, ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { message, Modal } from 'ant-design-vue'
import { cloneDeep } from 'lodash-es'
import { useMaterialArticle } from '@/store/material/article'
import { useDictionary } from '@/store/system/dictionary'
import Editor from '@/components/editor/index.vue'
import events from '@/utils/eventBus'
import { formDraft } from '@/utils/formDraft'
import { tabBeforeClose } from '@/utils/tabBeforeClose'

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false
  }
})

const ENABLE_DRAFT_SAVE = false

// 临时隐藏排序字段
const showSortField = false

const router = useRouter()
const route = useRoute()
const articleStore = useMaterialArticle()
const dictionaryStore = useDictionary()

const formRef = ref()
const editorKey = ref(0)
const isClosing = ref(false)
const isDraftLoaded = ref(false)
const currentPath = ref('')
const originForm = ref<any>(null)

const storeTypeList = ref<any[]>([])
const categoryList = ref<any[]>([])
const categoryListData = ref<any[]>([])

const isEdit = computed(() => !!route.params.id)

const INIT_RULE_FORM = {
  id: undefined as number | undefined,
  storeTypeCode: undefined as string | undefined,
  categoryCode: undefined as string | undefined,
  title: '',
  keyword: '',
  content: '',
  sortOrder: 0
}

const state = reactive({
  loading: {
    submit: false,
    detail: false
  },
  ruleForm: cloneDeep(INIT_RULE_FORM),
  rules: {
    title: [{ required: true, message: '请输入文章标题', trigger: 'blur' }],
    storeTypeCode: [{ required: true, message: '请选择店铺类型', trigger: 'change' }],
    categoryCode: [{ required: true, message: '请选择分类', trigger: 'change' }],
    content: [{ required: true, message: '请输入文章内容', trigger: 'blur' }]
  }
})

const changeStoreType = () => {
  categoryList.value = categoryListData.value.filter(item => item.code == state.ruleForm.storeTypeCode)
  state.ruleForm.categoryCode = ''
}

const goBack = () => {
  isClosing.value = true
  isDraftLoaded.value = false
  formDraft.remove(currentPath.value)
  events.emit('multiTab.close')
  // 刷新后 MultiTab 只剩当前标签，关闭会失败，强制移除并跳回列表
  setTimeout(() => {
    if (route.path.startsWith('/material/article/form') || route.path.startsWith('/material/article/detail')) {
      events.emit('multiTab.forceClose', currentPath.value)
      router.push('/material/article')
    }
  }, 150)
}

const onSubmit = () => {
  formRef.value.validate().then(async () => {
    state.loading.submit = true
    const param = cloneDeep(state.ruleForm)
    param.content = param.content.replace(
      /href="(?!https?:\/\/|mailto:|tel:|#|\/\/|javascript:|ftp:\/\/)([^"]+)"/g,
      'href="https://$1"'
    )
    if (!showSortField) {
      delete param.sortOrder
    }
    try {
      if (isEdit.value) {
        await articleStore.updateArticle(param)
      } else {
        await articleStore.createArticle(param)
      }
      message.success('操作成功')
      state.loading.submit = false
      isClosing.value = true
      isDraftLoaded.value = false
      formDraft.remove(currentPath.value)
      events.emit('multiTab.close')
      // 刷新后 MultiTab 只剩当前标签，关闭会失败，强制移除并跳回列表
      setTimeout(() => {
        if (route.path.startsWith('/material/article/form') || route.path.startsWith('/material/article/detail')) {
          events.emit('multiTab.forceClose', currentPath.value)
          router.push('/material/article')
        }
      }, 150)
    } catch {
      state.loading.submit = false
    }
  }).catch(() => {
    state.loading.submit = false
  })
}

const getStoreTypeList = async () => {
  try {
    const data: any = await dictionaryStore.getDict({ code: 'store_type' })
    storeTypeList.value = data || []
  } catch (err) {
    console.error('获取店铺类型失败', err)
    storeTypeList.value = []
  }
}

const getCategoryList = async () => {
  try {
    const data: any = await dictionaryStore.getDict({ code: 'article_category' })
    categoryListData.value = data || []
    categoryList.value = data || []
  } catch (err) {
    console.error('获取分类字典失败', err)
    categoryList.value = []
  }
}

const loadDetail = async (id: string) => {
  state.loading.detail = true
  try {
    const data: any = await articleStore.getArticleInfo(id)
    state.ruleForm = {
      id: data.id,
      storeTypeCode: data.storeTypeCode,
      categoryCode: data.categoryCode || '',
      title: data.title || '',
      keyword: data.keyword || '',
      content: (data.content || '').replace(
        /href="(?!https?:\/\/|mailto:|tel:|#|\/\/|javascript:|ftp:\/\/)([^"]+)"/g,
        'href="https://$1"'
      ),
      sortOrder: data.sortOrder || 0
    }
  } catch {
    // fallback
  } finally {
    state.loading.detail = false
  }
}

const initFormData = async () => {
  currentPath.value = route.fullPath
  editorKey.value++
  state.ruleForm = cloneDeep(INIT_RULE_FORM)
  const draft = ENABLE_DRAFT_SAVE ? formDraft.get(currentPath.value) : null
  if (draft) {
    state.ruleForm = cloneDeep(draft.formData)
    originForm.value = cloneDeep(draft.originForm)
    isDraftLoaded.value = true
  } else if (isEdit.value) {
    const id = route.params.id as string
    if (!id) {
      goBack()
      return
    }
    await loadDetail(id)
    originForm.value = cloneDeep(state.ruleForm)
  } else {
    originForm.value = cloneDeep(INIT_RULE_FORM)
  }
}

const handleTabClosed = (targetKey: string) => {
  if (targetKey === currentPath.value) {
    isClosing.value = true
    isDraftLoaded.value = false
    formDraft.remove(currentPath.value)
  }
}

const normalizeContent = (content: string = '') => {
  const text = content.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, '').trim()
  return text ? content : ''
}

const hasUnsavedChanges = () => {
  if (!originForm.value) return false
  return (
    state.ruleForm.title !== originForm.value.title ||
    state.ruleForm.storeTypeCode !== originForm.value.storeTypeCode ||
    state.ruleForm.categoryCode !== originForm.value.categoryCode ||
    state.ruleForm.keyword !== originForm.value.keyword ||
    state.ruleForm.sortOrder !== originForm.value.sortOrder ||
    normalizeContent(state.ruleForm.content) !== normalizeContent(originForm.value.content)
  )
}

const beforeCloseHook = () => {
  if (props.readonly || isClosing.value || !hasUnsavedChanges()) return Promise.resolve(true)
  return new Promise<boolean>((resolve) => {
    Modal.confirm({
      title: '提示',
      content: '有未保存的更改，确定要离开吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => resolve(true),
      onCancel: () => resolve(false)
    })
  })
}

const onSortChange = (delta: number) => {
  let val = (state.ruleForm.sortOrder || 0) + delta
  if (val < 0) val = 0
  if (val > 9999) val = 9999
  state.ruleForm.sortOrder = val
}

const onSortInput = (e: Event) => {
  const val = parseInt((e.target as HTMLInputElement).value, 10)
  state.ruleForm.sortOrder = isNaN(val) ? undefined as any : val
}

const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
  if (ENABLE_DRAFT_SAVE && !props.readonly && !isClosing.value && hasUnsavedChanges()) {
    e.preventDefault()
    e.returnValue = ''
  }
}

watch(
  () => route.fullPath,
  () => {
    initFormData()
  },
  { immediate: true }
)

onMounted(async () => {
  events.on('multiTab.closed', handleTabClosed)
  tabBeforeClose.register(currentPath.value, beforeCloseHook)
  window.addEventListener('beforeunload', beforeUnloadHandler)
  await getStoreTypeList()
  getCategoryList()
})

onBeforeUnmount(() => {
  events.off('multiTab.closed', handleTabClosed)
  tabBeforeClose.unregister(currentPath.value)
  window.removeEventListener('beforeunload', beforeUnloadHandler)
  if (ENABLE_DRAFT_SAVE) {
    if (!isClosing.value && hasUnsavedChanges()) {
      formDraft.set(currentPath.value, {
        formData: cloneDeep(state.ruleForm),
        originForm: cloneDeep(originForm.value)
      })
      tabBeforeClose.markUnsaved(currentPath.value, true)
    } else {
      formDraft.remove(currentPath.value)
      tabBeforeClose.clearUnsaved(currentPath.value)
    }
  } else {
    formDraft.remove(currentPath.value)
    tabBeforeClose.clearUnsaved(currentPath.value)
  }
})
</script>

<style scoped lang="less">
// ===== 表单 =====
:deep(.ant-form-item) {
  margin-bottom: 0;
  padding-bottom: 16px;
}

:deep(.ant-form-item-label) {
  margin-left: 0;
  margin-right: 5px;
  width: 80px;
  text-align: right;
  justify-content: flex-end;
  display: flex;
  align-items: center;
  height: 40px;
}

:deep(.ant-form-item-label > label) {
  font-size: 14px;
  line-height: 1;
  font-weight: 400;
}

:deep(.ant-input-affix-wrapper) {
  height: 40px;
  padding: 0 11px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

:deep(.ant-input-disabled) {
  height: 40px;
  border-color: #C5C5D3;
  border-radius: 4px;
}

:deep(.ant-select-selector) {
  height: 40px !important;
  display: flex;
  align-items: center;
  border-radius: 4px;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-input-affix-wrapper:not(:hover):not(.ant-input-affix-wrapper-focused)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-select:not(.ant-select-disabled):not(.ant-select-focused) .ant-select-selector:not(:hover)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(textarea.ant-input:not(:hover):not(:focus)) {
  border-color: #C5C5D3;
}

//textarea样式====>
:deep(.ant-input-affix-wrapper:has(textarea)) {
  height: auto !important;
  min-height: 40px;
}

:deep(textarea.ant-input) {
  min-height: 100px;
  resize: vertical;
  border-radius: 4px;
  padding: 8px 22px 8px 11px;
}

// 标题跨两行，撑满单元格高度；出现校验提示时 textarea 自身变矮，整体高度不变
.grid-title {

  :deep(.ant-form-item-row),
  :deep(.ant-form-item-control),
  :deep(.ant-form-item-control-input),
  :deep(.ant-form-item-control-input-content) {
    height: 100%;
  }

  :deep(.ant-input-affix-wrapper:has(textarea)) {
    height: 100% !important;
    min-height: 100% !important;
  }

  :deep(textarea.ant-input) {
    height: 100% !important;
    min-height: 100% !important;
    resize: none;
  }
}

:deep(.grid-title .ant-input-textarea) {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;

  &[data-count]::after {
    content: attr(data-count);
    position: absolute;
    right: 10px;
    bottom: 5px;
    z-index: 1;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    pointer-events: none;
    background: #fff;
    padding-left: 4px;
    line-height: 1;
  }
}

:deep(.ant-input-data-count) {
  display: none;
}

// 仅 textarea 的清除图标需要固定到第一行，普通 a-input 保持默认 flex 布局避免 x 与 count 重叠
:deep(.ant-input-textarea .ant-input-clear-icon) {
  position: absolute;
  top: 12px;
  right: 10px;
  bottom: auto;
  margin-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  line-height: 1;
}

//<====textarea样式

// ===== 三列网格布局 =====
.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, auto);
  gap: 0 16px;

  .grid-storeType {
    grid-column: 1;
    grid-row: 1;
  }

  .grid-category {
    grid-column: 2;
    grid-row: 1;
  }

  .grid-title {
    grid-column: 3;
    grid-row: 1 / 3;
  }

  .grid-keyword {
    grid-column: 1;
    grid-row: 2;
  }

  .grid-sort {
    grid-column: 2;
    grid-row: 2;
  }
}

.grid-content {
  padding-bottom: 16px;

  :deep(.ant-form-item-label) {
    display: none;
  }
}

.sort-input-number {
  display: flex;
  border: 1px solid #C5C5D3;
  border-radius: 4px;
  width: fit-content;

  .sort-btn {
    width: 40px;
    height: 40px;
    background: #fff;
    border: none;
    cursor: pointer;

    &:disabled {
      background: #f5f5f5;
      color: #bbb;
      cursor: not-allowed;
    }
  }

  .sort-input {
    width: 80px;
    height: 40px;
    border: none;
    border-left: 1px solid #C5C5D3;
    border-right: 1px solid #C5C5D3;
    text-align: center;
    outline: none;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

.form-footer {
  display: flex;
  justify-content: center;
  padding-top: 46px;

  :deep(.ant-btn) {
    width: 120px;
    height: 40px;
    border-radius: 20px !important;
    font-size: 16px;
  }

  :deep(.ant-btn-primary) {
    background: #0D479C;
    border-color: #0D479C;
    color: #fff;
  }

  :deep(.ant-btn-default) {
    border: 1px solid #0C418F;
    color: #0C418F;
  }
}

// ===== 基础工具类 =====
.ml10 {
  margin-left: 10px;
}

.space-gap {
  height: 16px;
}

.draft-tip {
  padding: 0 0 8px;
  text-align: right;
}
</style>
